import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';

import CaseListDropDown from 'components/CaseListDropDown/CaseListDropDown';
import { ModalHeader } from 'shared/ModalHeader';
import { manualAutomationTrigger } from 'services/Settings';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import GlobalTooltip from 'components/GlobalTooltip';
import {
  CREATE_EVENT,
  CREATE_TASK,
  SEND_SECURE_MESSAGE,
  SEND_TEXT_MESSAGE,
  CREATE_FOLDER_STURCTURE,
  CREATE_CASE_FILE,
  SHARE_FORM,
} from 'constants/automationConstants';

const RunAutomationModal = ({ visible, onHide, automationData }) => {
  const [selectedCase, setSelectedCase] = useState(null);
  const [selectedActions, setSelectedActions] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const { addToast } = useToast();

  useEffect(() => {
    if (visible) {
      setSelectedCase(null);
      setSelectedActions([]);
    }
  }, [visible]);

  const executionSequence = automationData?.workflow?.execution_sequence || [];

  const isActionDisabled = (action) => {
    const { action_code, action_description } = action;
    const body = action_description?.body || {};

    if ((action_code === 'ACT_EVENT_CREATE' || action_code === 'ACT_TASK_CREATE') && body?.event_with_schedule_date === true) {
      return true;
    }
    return false;
  };

  const countFoldersRecursively = (folderTree) => {
    let count = 0;

    const countInNode = (node) => {
      count += 1;

      if (node.children && Array.isArray(node.children)) {
        node.children.forEach((child) => countInNode(child));
      }
    };

    if (folderTree?.root && Array.isArray(folderTree.root)) {
      folderTree.root.forEach((rootNode) => {
        countInNode(rootNode);
      });
    }

    return count;
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      if (automationData?.workflow?.execution_sequence) {
        const enabledIndices = automationData.workflow.execution_sequence
          ?.map((action, index) => ({ action, index }))
          ?.filter(({ action }) => !isActionDisabled(action))
          ?.map(({ index }) => index);
        setSelectedActions(enabledIndices || []);
      }
    } else {
      setSelectedActions([]);
    }
  };

  const handleClearSelection = () => {
    setSelectedActions([]);
  };

  const handleRunSelected = async () => {
    if (!selectedCase || selectedActions?.length === 0) {
      return;
    }

    try {
      setIsRunning(true);

      const selectedActionObjects = selectedActions?.map((index) => executionSequence[index]);

      const payload = {
        is_lead: selectedCase?.is_lead || false,
        case_id: selectedCase?.id,
        workflow_title: automationData?.workflow_title,
        version: automationData?.version,
        sk: automationData?.sk,
        pk: automationData?.pk,
        execution_sequence: selectedActionObjects,
      };

      const response = await manualAutomationTrigger(payload);

      if (response?.data) {
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.AUTOMATION_RUN_SUCCESS);
        onHide();
      }
    } catch (error) {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, toastConstant.message.AUTOMATION_RUN_FAILED);
    } finally {
      setIsRunning(false);
    }
  };

  const enabledActionsCount = executionSequence?.filter((action) => !isActionDisabled(action))?.length || 0;
  const isAllSelected = enabledActionsCount > 0 && selectedActions?.length === enabledActionsCount;

  const handleActionToggle = (actionIndex) => {
    setSelectedActions((prev) => {
      if (prev?.includes(actionIndex)) {
        return prev?.filter((index) => index !== actionIndex);
      } else {
        return [...prev, actionIndex];
      }
    });
  };

  const getActionLabelFromConstants = (action, actionCode) => {
    switch (actionCode) {
      case 'ACT_SEND_SECURE_MESSAGE':
        return SEND_SECURE_MESSAGE;
      case 'ACT_EVENT_CREATE':
        return CREATE_EVENT;
      case 'ACT_TASK_CREATE':
        return CREATE_TASK;
      case 'ACT_SEND_TEXT_MESSAGE':
        return SEND_TEXT_MESSAGE;
      case 'ACT_CREATE_DOCUMENT':
        return CREATE_CASE_FILE;
      case 'ACT_SHARE_FORM':
        return SHARE_FORM;
      case 'ACT_BULK_UPLOAD_FOLDER':
        return CREATE_FOLDER_STURCTURE;
      default:
        return action?.action_label || 'Unknown Action';
    }
  };

  const getActionDisplayText = (action) => {
    const { action_code, action_description } = action;
    const body = action_description?.body || {};

    switch (action_code) {
      case 'ACT_SEND_SECURE_MESSAGE':
        return `Subject: ${body.data?.body?.subject || 'No subject'}`;
      case 'ACT_EVENT_CREATE':
        return `Event Title: ${body?.title || 'No title'}`;
      case 'ACT_TASK_CREATE':
        return `Task Name: ${body?.task_name || 'No task name'}`;
      case 'ACT_SEND_TEXT_MESSAGE':
        return `Message: ${body?.message_body || 'No message'}`;
      case 'ACT_CREATE_DOCUMENT':
        return `Document Name: ${body?.document_name || 'No document name'}`;
      case 'ACT_SHARE_FORM':
        return `Form Name: ${body?.form_name || 'No form name'}`;
      case 'ACT_BULK_UPLOAD_FOLDER':
        const folderCount = countFoldersRecursively(body?.folder_tree);
        return `${folderCount} Folders Total`;
      case 'ACT_SEND_EMAIL':
        return `Subject: ${body?.subject || 'No subject'}`;
      default:
        return 'No description available';
    }
  };

  return (
    <Dialog header={ModalHeader} visible={visible} onHide={onHide} style={{ width: '50vw' }} modal closable={false}>
      <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
        <div>
          <div className="modal-heading F-size16 text-bold">{'Run Automation Manually'}</div>
          <div className="text-muted F-size12 mt-1">Select and run automation actions manually</div>
        </div>
      </div>
      <div className="p-fluid">
        <div className="col-6 mb-3">
          <label className="p-medium">Choose Case/Lead</label>
          <CaseListDropDown
            value={selectedCase}
            onChange={(selectedOption) => setSelectedCase(selectedOption)}
            placeholder="Select Case/Lead"
            noOptionsMessage="No Cases Found"
            caseOnly={!automationData?.is_lead_automation ? true : false}
            is_case_list={automationData?.is_lead_automation === true ? false : true}
            paFilter={automationData?.practice_area}
          />
        </div>

        <div className="mb-4">
          <div className="mb-2">
            <label className="p-medium">Choose actions to run</label>
          </div>
          <div className="d-flex justify-content-between align-items-center mb-0 pe-2">
            <div className="form-check d-flex align-items-center me-3 ps-0">
              <Checkbox inputId="selectAll" checked={isAllSelected} onChange={(e) => handleSelectAll(e.checked)} className="me-2" />
              <label htmlFor="selectAll" className="form-check-label cursor-pointer">
                Select All
              </label>
            </div>
            <button type="button" className="btn btn-link btn-sm" onClick={handleClearSelection} disabled={selectedActions?.length === 0}>
              Clear Selection
            </button>
          </div>

          <div>
            <div
              className="pt-1 overflow-y-auto pe-2"
              style={{
                maxHeight: '300px',
              }}
            >
              {executionSequence?.map((action, index) => {
                const isDisabled = isActionDisabled(action);
                return (
                  <div key={index} className="mb-3 py-2 px-3 inner-card">
                    <div className="form-check d-flex align-items-start ps-0 pe-2">
                      <GlobalTooltip tooltip={isDisabled ? 'Action is based on trigger event schedule date' : ''} position="top">
                        <Checkbox
                          inputId={`action-${index}`}
                          checked={selectedActions?.includes(index)}
                          onChange={() => handleActionToggle(index)}
                          className="me-3 mt-1"
                          disabled={isDisabled}
                        />
                      </GlobalTooltip>
                      <label
                        htmlFor={`action-${index}`}
                        className={`form-check-label flex-grow-1 ${isDisabled ? 'text-muted' : 'cursor-pointer'}`}
                      >
                        <b className={`black-700  ${isDisabled ? 'text-muted' : ''}`}>
                          {getActionLabelFromConstants(action, action?.action_code)}
                        </b>
                        <div className="text-muted small">{getActionDisplayText(action)}</div>
                      </label>
                    </div>
                  </div>
                );
              })}

              {executionSequence?.length === 0 && (
                <div className="text-center text-muted py-4">No actions available for this automation</div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="footer-btn">
        <div className="col-12 d-flex justify-content-end">
          <Button label="Cancel" className="p-button-secondary outline" onClick={onHide} />
          <Button
            label={isRunning ? 'Running...' : `Run Selected (${selectedActions.length})`}
            className="p-button-secondary ms-2"
            disabled={selectedActions.length === 0 || !selectedCase || isRunning}
            onClick={handleRunSelected}
            loading={isRunning}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default RunAutomationModal;
